import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { LinkProps } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { LogoBrandV2 } from "@/libraries/wpl/foundations/src";

import { LogoLinkContainer } from "./global-footer-top-logo";

// ==================================================================
// Style Functions
// ==================================================================
const listOfLinksColumnStyle = css({
    position: "relative",
    minHeight: "1px",
    padding: "0",
    margin: "0",
    fontSize: "100%",
    verticalAlign: "baseline",
    display: "block",
    color: wacToken("color.extended.blue1000"),
    boxSizing: "border-box",
    width: "100%"
});

const listOfLinksUlStyle = css({
    marginBlockEnd: "0",
    paddingInlineStart: "20px",
    listStyle: "none"
});

const listOfLinksUlLiStyle = css({
    "marginBlockEnd": "19px",
    "marginBlockStart": token("space.0"),
    "listStyleType": "none",
    "marginInlineStart": "-20px",
    "color": "inherit",
    "fontSize": "1rem",
    "lineHeight": "14px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const listOfLinksUlLiAnchorTagStyle = css({
    "fontSize": "0.875rem",
    "letterSpacing": "0.42px",
    "fontFamily": "Charlie Display",

    "&:hover": {
        textDecoration: "underline",
        textShadow: "none"
    },

    "&:visited,&:focus": {
        textShadow: "none"
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")} !important`,
        outlineOffset: "2px"
    }
});

const atlassianLinksLiAnchorTagStyle = css({
    fontWeight: "700",
    fontFamily: "Charlie Text",
    letterSpacing: "normal"
});

const headerContainerStyle = css({
    height: "32px",
    marginBlockStart: token("space.0"),
    marginBlockEnd: token("space.400"),
    display: "flex",
    alignItems: "center"
});

const listOfLinksColumnHeaderStyle = css({
    fontSize: "13px",
    lightHeight: "1.25",
    textTransform: "uppercase",
    color: `${wacToken("color.primary.black")} !important`,
    fontWeight: "700 !important",
    fontFamily: "Charlie Text"
});

const atlassianLogoStyle = css({
    height: "32px",
    marginBlockEnd: token("space.400")
});

const bottomViewAllLinkDivStyle = css({
    "fontWeight": "500",
    "marginBlockStart": token("space.400"),
    "paddingBlock": "8px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const bottomViewAllLinkLiAnchorStyle = css({
    "color": wacToken("color.primary.black"),
    "fontSize": "0.875rem",
    "textDecoration": "none",
    "letterSpacing": "0.42px",
    "fontFamily": "Charlie Display",
    "&:hover,&:visited,&:focus": {
        textDecoration: "underline"
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")} !important`,
        outlineOffset: "2px"
    }
});

// ==================================================================
// Logo Link Props
// ==================================================================
export type LinkListProps = {
    readonly headerText?: string;
    readonly listOfLinks: LinkProps[];
    readonly bottomViewAllLink?: LinkProps;
    readonly logoType?: LogoBrandV2;
};

// ==================================================================
// Rendering Functions
// ==================================================================
export const LinkListContainer = ({
    headerText,
    listOfLinks,
    bottomViewAllLink,
    logoType
}: LinkListProps) => {
    return (
        <div css={listOfLinksColumnStyle}>
            {headerText ? (
                <div css={headerContainerStyle}>
                    <h2
                        css={listOfLinksColumnHeaderStyle}
                        className="eyebrow-md"
                    >
                        {headerText.trim()}
                    </h2>
                </div>
            ) : (
                <div css={atlassianLogoStyle}>
                    <LogoLinkContainer logoType={logoType} />
                </div>
            )}
            <ul css={listOfLinksUlStyle}>
                {listOfLinks.map((listOfLink) => (
                    <li
                        css={listOfLinksUlLiStyle}
                        key={listOfLink.text.trim().replace(" ", "-")}
                    >
                        <Link
                            css={[
                                listOfLinksUlLiAnchorTagStyle,
                                !headerText
                                    ? atlassianLinksLiAnchorTagStyle
                                    : null
                            ]}
                            text={listOfLink.text}
                            href={listOfLink.href}
                            type="anchor"
                            bgColor={"bgColored"}
                            onClick={listOfLink.onClick}
                            className={listOfLink.className}
                            aria-label="footer-link"
                        />
                    </li>
                ))}
            </ul>
            {bottomViewAllLink && (
                <div css={bottomViewAllLinkDivStyle}>
                    <Link
                        css={bottomViewAllLinkLiAnchorStyle}
                        text={bottomViewAllLink.text}
                        href={bottomViewAllLink.href}
                        type="primary"
                        bgColor={"bgColored"}
                        onClick={bottomViewAllLink.onClick}
                        className={bottomViewAllLink.className}
                        aria-label="footer-link"
                    />
                </div>
            )}
        </div>
    );
};
